import { t } from "@lingui/macro";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { ScrollView } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { PackageDetail } from "@/features/Packages/PackageDetail";
import { PackageDetailSkeleton } from "@/features/Packages/PackageDetail/components/PackageDetailSkeleton";
import { MembershipsStackNavigatorParamList } from "@/navigators/ProfileStackNavigator/MembershipsStackNavigator";
import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { usePackageDisplayName } from "@whoosh/features/src/Packages";

import { MembershipDetailScreenQuery } from "./__generated__/MembershipDetailScreenQuery.graphql";

export const MembershipDetailScreen = () => {
  const route =
    useRoute<
      RouteProp<MembershipsStackNavigatorParamList, "MembershipDetail">
    >();

  const membershipId = route.params.membershipId;

  const [queryRef, loadQuery] = useQueryLoader<MembershipDetailScreenQuery>(
    MembershipDetailScreenQueryString
  );

  useEffect(() => {
    if (membershipId) {
      loadQuery(
        { membershipId, packageType: "MEMBERSHIP" },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [loadQuery, membershipId]);

  return (
    <Suspense fallback={<PackageDetailSkeleton />}>
      {queryRef ? <MembershipDetailScreenInner queryRef={queryRef} /> : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<MembershipDetailScreenQuery>;
};

const MembershipDetailScreenInner = ({ queryRef }: InnerProps) => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();

  const data = usePreloadedQuery(MembershipDetailScreenQueryString, queryRef);

  const onBackPress = () => {
    navigation.goBack();
  };

  const membershipName = usePackageDisplayName(data.membership?.package);

  return (
    <Box
      bg="surface-primary"
      flex={1}
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
    >
      <Container flex={1} paddingHorizontal={0}>
        <ScreenHeader
          title={membershipName}
          leftItem={
            <Button
              variant="secondary"
              accessibilityLabel={t`Back Button`}
              leftIcon={<ArrowLeftIcon />}
              onPress={onBackPress}
              testID="mobile-header-back-button"
            />
          }
          testID="membership-screen-header"
        />
        <ScrollView style={{ paddingTop: 16 }}>
          <PackageDetail type="MEMBERSHIP" memberPackageRef={data.membership} />
        </ScrollView>
      </Container>
    </Box>
  );
};

const MembershipDetailScreenQueryString = graphql`
  query MembershipDetailScreenQuery(
    $membershipId: ID!
    $packageType: PackageType!
  ) {
    membership: node(id: $membershipId) {
      ...PackageDetail_memberPackage
      ... on MemberPackage {
        package {
          ...usePackageDisplayName_package
        }
      }
    }
  }
`;
